var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-empty-function */
import { useAlert } from 'components/alert/AlertStack';
import { useEffect, useLayoutEffect, useQuery, useMutation, useState, useRef } from 'libs/react';
import { Content } from 'components/Content';
import { Screen } from 'components/Screen';
import { React, classNames } from 'libs/react';
import { useDomain } from 'components/domain/DomainProvider';
import { MutationForm } from 'components/form/graphql/MutationForm';
import { useModal } from 'components/modal/ModalStack';
import { Icon } from 'components/Icon';
import { Text } from 'components/Text';
import { Button } from 'components/Button';
import { CreateDealMutation } from 'graphql/Deal/mutations/CreateDealMutation';
import { UpdateDealPublicMutation } from 'graphql/Deal/mutations/UpdateDealPublicMutation';
import { DealPublicQuery } from 'graphql/Deal/queries/DealPublicQuery';
import { ApolloError, gql } from '@apollo/client';
import { StoreHeader } from './StoreHeader';
import { ContactSection } from './StoreContact';
import { ProductSection } from './StoreProduct';
import { dealRequiresPlannedDate, selectionRequiresPlannedDate } from 'helpers/requiresPlannedDate';
import { StoreQuoteSummaryPanel } from './StoreQuoteSummaryPanel';
import { StoreConfirmation } from './StoreConfirmation';
import { StoreEmailModal } from './StoreEmailModal';
import { StorePaymentPanel } from './StorePaymentPanel';
import { buildOptionsPayload } from './StoreProposedOptions';
import { buildStoreGTMItems, buildStoreStripeReturnPath, sendStoreGTMEvent } from './storeGtm';
import { loadCampaignAttribution } from 'components/domain/campaignAttribution';
import { pushEnhancedConversionUserData } from 'components/domain/gtmConsent';
import { getConfigurationFieldValue } from 'helpers/ConfigurationField';
import { outOfStockFields } from 'helpers/inventory';
import { ApproveDealMutation } from 'graphql/Deal/mutations/ApproveDeal';
import { EmptyData } from 'components/EmptyData';
import { isPublicAccountUnavailableError } from 'helpers/isPublicAccountUnavailableError';
import { Header } from 'components/Header';
import { Spinner } from 'components/Spinner';
import { StoreCheckoutStickyBar } from './components/StoreCheckoutStickyBar';
import { StorePromoCodeField } from './components/StorePromoCodeField';
import { RemovePromoCodeMutation } from 'graphql/Deal/mutations/RemovePromoCodeMutation';
import { captureStorePromoCode, clearStorePromoCode, loadStorePromoCode, promoCodeErrorReason, promoCodeReasonMessage } from './storePromoCode';
import { StoreProposedOptionsSection } from './StoreProposedOptionsSection';
import { StoreVoucherFieldsSection } from './StoreVoucherFieldsSection';
import { isVoucherFieldsMissingError } from './storeVoucherFields';
import { buildStoreSteps, getPrevStepId, hasProposedOptionsInSelection, hasVoucherFieldsInSelection } from './storeSteps';
import { applyStoreSelectionToDeal, buildStoreDealUpdateInput, removeDealItem, removeDealItemOption } from './storeDealUpdate';
import { buildGiftDealInput, validateGiftDealInput } from './storeGift';
import { clearStoreCart, readInitialStoreCart, saveStoreCart, selectedItemsFromDeal } from './storeCart';
import { useParams, useLocation } from 'react-router-dom';
import { PartnerStoreQuery } from 'graphql/Client/queries/PartnerStoreQuery';
import { storeHomePath, storeLanguageSwitchPath } from './storePath';
import { readStoreHeadline, storeDocumentTitle } from './storeDocumentTitle';
import { findStoreCategoryTitle } from './storeCategories';
import { directSaleCartHasInactiveProduct, isProductInactiveError } from './directSaleCartProducts';
import i18n from 'components/translation/i18n';
import './StoreScreen.scss';
import { applyBrandColors, resolvePrimaryColor } from 'helpers/primaryColor';
/**
 * @component StoreScreen
 * @since 1.0.0
 */
export function StoreScreen() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    const alert = useAlert();
    const modal = useModal();
    const { lang, partnerSlug: partnerSlugParam } = useParams();
    const partnerSlug = (partnerSlugParam === null || partnerSlugParam === void 0 ? void 0 : partnerSlugParam.toLowerCase()) || null;
    const location = useLocation();
    const isStoreMode = location.pathname.startsWith('/store');
    const urlDealId = new URLSearchParams(location.search).get('dealId');
    const sessionId = new URLSearchParams(location.search).get('session_id');
    const paymentId = new URLSearchParams(location.search).get('payment_id');
    const [currentStepId, setCurrentStepId] = useState(urlDealId ? 'contact' : 'products');
    const [storeItems, setStoreItems] = useState(() => readInitialStoreCart(urlDealId).items);
    const [, setHasOnlyFinishedProducts] = useState(true);
    const [dealId, setDealId] = useState(() => readInitialStoreCart(urlDealId).dealId);
    const [addressFilled, setAddressFilled] = useState(false);
    const [addressError, setAddressError] = useState(false);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const [checkoutSessionKey, setCheckoutSessionKey] = useState(null);
    const [isUpdatingDeal, setIsUpdatingDeal] = useState(false);
    const formValuesRef = useRef(null);
    const stripeReturnHandledRef = useRef(false);
    const beginCheckoutSentRef = useRef(false);
    const purchaseSentRef = useRef(false);
    const restorePaymentStepRef = useRef(true);
    const hydratedFromDealRef = useRef(storeItems.length > 0);
    const storeCartHost = partnerSlug
        ? `${window.location.hostname}/p/${partnerSlug}`
        : window.location.hostname;
    const goToStep = (stepId) => {
        setCurrentStepId((current) => {
            if (current === 'payment' && stepId !== 'payment' && !paymentComplete) {
                restorePaymentStepRef.current = false;
            }
            return stepId;
        });
    };
    useLayoutEffect(() => {
        var _a;
        for (const selector of ['.store-screen', '.public-layout']) {
            (_a = document.querySelector(selector)) === null || _a === void 0 ? void 0 : _a.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [currentStepId]);
    // Set primary color from domain
    const { host, name: domainName, logo: domainLogo, icon, options, configurationFields, captcha } = useDomain();
    // Partner store (/store/<lang>/p/<slug>): its own logo and catalog.
    const [partnerData, partnerReady, partnerError] = useQuery(PartnerStoreQuery, {
        variables: { input: { slug: partnerSlug !== null && partnerSlug !== void 0 ? partnerSlug : '' } },
        skip: !partnerSlug
    });
    const partnerStore = partnerSlug ? (_a = partnerData === null || partnerData === void 0 ? void 0 : partnerData.partnerStore) !== null && _a !== void 0 ? _a : null : null;
    const logo = (_b = partnerStore === null || partnerStore === void 0 ? void 0 : partnerStore.image) !== null && _b !== void 0 ? _b : domainLogo;
    applyBrandColors({
        primary: resolvePrimaryColor(options.color, '#F05B22'),
        accent: options.accentColor,
        surface: options.surfaceColor
    });
    // change favicon with logo
    useEffect(() => {
        var _a;
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
            favicon.href = (_a = icon === null || icon === void 0 ? void 0 : icon.url) !== null && _a !== void 0 ? _a : '/favicon-16x16.png';
        }
    }, [icon === null || icon === void 0 ? void 0 : icon.url]);
    useEffect(() => {
        document.body.classList.toggle('loaded', true);
    }, []);
    let language = lang !== null && lang !== void 0 ? lang : i18n.language.substring(0, 2);
    if (lang) {
        // force EN if not FR
        if (lang !== 'fr' && lang !== 'en') {
            language = 'en';
        }
        i18n.changeLanguage(lang);
        language = lang;
    }
    // Unknown or disabled partner store: back to the account store.
    useEffect(() => {
        if (partnerSlug && partnerReady && !partnerError && !partnerStore) {
            window.location.replace(`/${storeHomePath(language)}`);
        }
    }, [partnerSlug, partnerReady, partnerError, partnerStore, language]);
    const hasStoreForm = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], 'HAS_DYNAMIC_SALES_FORM');
    const hasDirectSales = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], 'HAS_DIRECT_SALES');
    const dynamicSalesFormSaleItemGroup = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], isStoreMode
        ? 'DIRECT_SALES_FORM_SALE_ITEM_GROUP'
        : 'DYNAMIC_SALES_FORM_SALE_ITEM_GROUP');
    const dynamicSalesFormContactFields = getConfigurationFieldValue(configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], isStoreMode
        ? 'DIRECT_SALES_FORM_CONTACT_FIELDS'
        : 'DYNAMIC_SALES_FORM_CONTACT_FIELDS');
    const requiresAddress = !!(dynamicSalesFormContactFields === null || dynamicSalesFormContactFields === void 0 ? void 0 : dynamicSalesFormContactFields.includes('ADDRESS'));
    useEffect(() => {
        if (addressFilled) {
            setAddressError(false);
        }
    }, [addressFilled]);
    useEffect(() => {
        if (!requiresAddress) {
            setAddressFilled(true);
        }
    }, [requiresAddress]);
    const [data, , saleItemsError, , refetchItems] = useQuery(gql `
      query SaleItemsList($input: SaleItemsInput!) {
        saleItems(input: $input) {
          id
          sku
          title
          infos
          summary
          configuration
          price
          requiresPlannedDate
          trackInventory
          outOfStockPolicy
          availableQuantity

          group {
            id
            order
            translations {
              language {
                code
              }
              title
            }
          }

          groups {
            id
            order
            translations {
              language {
                code
              }
              title
            }
          }

          image {
            id
            image {
              url
              thumbnail
              thumbnailWidth
              display
              displayWidth
            }
          }

          images {
            id
            image {
              url
              thumbnail
              thumbnailWidth
              display
              displayWidth
            }
          }

          translations {
            language {
              code
            }
            title
            infos
            summary
          }

          fieldValues {
            value
            field {
              id
              name
              title
              type
              promote
              visibility
              group {
                id
                category
              }
            }
          }

          files {
            id
            visibility
            file {
              url
              filename
            }
            language {
              code
            }
          }

          promotions {
            type
            value
          }

          voucherFieldGroups {
            id
            title
            fields {
              id
              type
              title
              infos
              options
              active
              required
              order
              defaultValue
              defaultValueLocked
              translations {
                title
                infos
                language {
                  code
                }
              }
            }
          }

          bonuses {
            type
            value
            separateVoucher
            order
            active
            conditions
          }

          options {
            id
            price
            quantity
            order
            proposed
            included
            required
            recommended
            quantityEditable
            item {
              id
              title
              summary
              infos
              translations {
                language {
                  code
                }
                title
                summary
              }
              image {
                id
                image {
                  url
                  thumbnail
                  thumbnailWidth
                  display
                  displayWidth
                }
              }
              images {
                id
                image {
                  url
                  thumbnail
                  thumbnailWidth
                  display
                  displayWidth
                }
              }
              groups {
                id
                hasUniqueOptionRestriction
              }
              fieldValues {
                value
                field {
                  id
                  name
                  title
                  type
                  promote
                  visibility
                  group {
                    id
                    category
                  }
                }
              }
              files {
                id
                visibility
                file {
                  url
                  filename
                }
                language {
                  code
                }
              }
            }
            relations {
              type
              quantity
              relatedSaleItem {
                id
              }
            }
          }
        }
      }
    `, {
        variables: {
            input: Object.assign({ optionConfiguration: 'ITEM_ONLY', visibility: ['GLOBAL', 'PUBLIC'], active: true, order: 'order:ASC', configuration: 'FINISHED' }, (partnerSlug ? { partner: partnerSlug } : {}))
        },
        fetchPolicy: 'cache-and-network'
    });
    const [dealData, dealReady, dealError, , refetchDeal] = useQuery(DealPublicQuery, {
        variables: { input: { id: dealId !== null && dealId !== void 0 ? dealId : '' } },
        skip: !dealId,
        fetchPolicy: 'cache-and-network'
    });
    const [createDeal] = useMutation(CreateDealMutation);
    const [updateDeal] = useMutation(UpdateDealPublicMutation);
    const [removePromoCode] = useMutation(RemovePromoCodeMutation);
    // A `?promo=CODE` link: the code is sent when the cart is created.
    useEffect(() => {
        if (host) {
            captureStorePromoCode(host);
        }
    }, [host]);
    const deal = dealData === null || dealData === void 0 ? void 0 : dealData.deal;
    const saleItems = (_c = data === null || data === void 0 ? void 0 : data.saleItems) !== null && _c !== void 0 ? _c : [];
    const hasOptionsStep = hasProposedOptionsInSelection(storeItems, saleItems);
    const hasDetailsStep = hasVoucherFieldsInSelection(storeItems, saleItems);
    // The details step needs the saved cart lines (one form per voucher)
    const afterSelectionStepId = hasDetailsStep
        ? 'details'
        : 'contact';
    const showPlannedDate = selectionRequiresPlannedDate(storeItems, saleItems) ||
        (currentStepId === 'contact' && deal
            ? dealRequiresPlannedDate((_d = deal.items) !== null && _d !== void 0 ? _d : [], saleItems)
            : false);
    const paymentStepLabel = paymentComplete ? t('Confirmation') : t('Payment');
    const storeSteps = buildStoreSteps(hasOptionsStep, paymentStepLabel, hasDetailsStep);
    // A ?product= or ?category= link has its own title, like the server
    // rendered one: Google reads the title after the page has run.
    const searchParams = new URLSearchParams(location.search);
    const titleSku = (_e = searchParams.get('product')) === null || _e === void 0 ? void 0 : _e.toLowerCase().trim();
    const titleProduct = titleSku
        ? saleItems.find((item) => item.sku.toLowerCase().trim() === titleSku)
        : null;
    const pageTitle = currentStepId !== 'products'
        ? null
        : titleProduct
            ? ((_g = (_f = titleProduct.translations) === null || _f === void 0 ? void 0 : _f.find((entry) => entry.language.code === language)) === null || _g === void 0 ? void 0 : _g.title) || titleProduct.title
            : findStoreCategoryTitle(saleItems, searchParams.get('category'), language);
    const documentTitle = storeDocumentTitle({
        businessName: (partnerStore === null || partnerStore === void 0 ? void 0 : partnerStore.name) || domainName,
        headline: readStoreHeadline(configurationFields, language),
        pageTitle
    });
    const hasActivePayments = (_j = (_h = deal === null || deal === void 0 ? void 0 : deal.payments) === null || _h === void 0 ? void 0 : _h.some((payment) => payment.status === 'ACTIVE')) !== null && _j !== void 0 ? _j : false;
    const isStripePayment = ((_k = deal === null || deal === void 0 ? void 0 : deal.paymentIntegration) === null || _k === void 0 ? void 0 : _k.type) === 'stripe' &&
        !!((_l = deal === null || deal === void 0 ? void 0 : deal.paymentIntegration) === null || _l === void 0 ? void 0 : _l.accountId);
    // Mews Payments: the partner's property, else the account's
    const isMewsPayment = ((_m = deal === null || deal === void 0 ? void 0 : deal.paymentIntegration) === null || _m === void 0 ? void 0 : _m.type) === 'mews_payment';
    const isOnlinePayment = isStripePayment || isMewsPayment;
    const stripeReturnPath = dealId != null
        ? buildStoreStripeReturnPath(language, dealId, partnerSlug)
        : '';
    const showPaymentStep = !!checkoutSessionKey &&
        currentStepId === 'payment' &&
        !!deal &&
        !paymentComplete;
    const showConfirmationStep = currentStepId === 'payment' && dealReady && paymentComplete;
    const orderEmail = (_q = (_p = (_o = deal === null || deal === void 0 ? void 0 : deal.contact) === null || _o === void 0 ? void 0 : _o.primaryEmail) === null || _p === void 0 ? void 0 : _p.email) !== null && _q !== void 0 ? _q : undefined;
    const checkoutSubmitLabel = isOnlinePayment && hasActivePayments ? t('Order now') : t('Confirm order');
    const showCheckoutLoading = currentStepId === 'payment' && !paymentComplete && !checkoutSessionKey;
    const prevStepId = showConfirmationStep
        ? null
        : getPrevStepId(currentStepId, hasOptionsStep, hasDetailsStep);
    function handleProductsContinue() {
        if (hasOptionsStep) {
            goToStep('options');
        }
        else if (dealId) {
            void persistSelectionAndGoTo(afterSelectionStepId);
        }
        else {
            openEmailModal();
        }
    }
    /**
     * @function openEmailModal
     * @since 1.0.0
     * @hidden
     */
    function openEmailModal() {
        modal.present({
            title: t('Continue with your order'),
            picto: 'envelope',
            className: 'store-email-modal',
            modal: (React.createElement(StoreEmailModal, { captcha: captchaConfig, language: language, configurationFields: configurationFields, onConfirm: handleCreateDeal }))
        });
    }
    /**
     * @function handleCreateDeal
     * @since 1.0.0
     * @hidden
     */
    function handleCreateDeal(email, captchaToken, marketingOptIn) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const saleItems = (_a = data === null || data === void 0 ? void 0 : data.saleItems) !== null && _a !== void 0 ? _a : [];
            const optionsPayload = buildOptionsPayload(storeItems, saleItems);
            const result = yield createDeal({
                variables: {
                    input: Object.assign(Object.assign(Object.assign({ type: 'QUOTE', title: t('Direct Sale from ') + email, draft: false, status: 'ACTIVE', stage: 'DRAFT', contact: { email }, currency: 'ef1f4eb475d211eabc550242ac130001', language: language, items: storeItems.map((item) => {
                            var _a, _b;
                            const itemOptions = (_b = (_a = optionsPayload.find((entry) => entry.itemId === item.id)) === null || _a === void 0 ? void 0 : _a.options) !== null && _b !== void 0 ? _b : [];
                            return {
                                item: item.id,
                                quantity: item.quantity,
                                options: itemOptions.map((opt) => ({
                                    item: opt.itemId,
                                    quantity: opt.quantity,
                                    price: opt.price,
                                    proposed: false
                                }))
                            };
                        }), priceDisplay: 'ALL', directSale: true }, (partnerStore ? { partner: partnerStore.id } : {})), { captchaToken: captchaToken || undefined, marketingOptIn: marketingOptIn || undefined, promoCode: loadStorePromoCode(host) }), loadCampaignAttribution(host))
                }
            }).catch((error) => {
                if (handleOutOfStock(error)) {
                    modal.dismiss();
                    return null;
                }
                throw error;
            });
            if (!result) {
                return;
            }
            const createdId = (_c = (_b = result === null || result === void 0 ? void 0 : result.data) === null || _b === void 0 ? void 0 : _b.createDeal) === null || _c === void 0 ? void 0 : _c.id;
            if (!createdId) {
                throw new Error('deal_creation_failed');
            }
            setDealId(createdId);
            syncDealIdInUrl(createdId);
            goToStep(afterSelectionStepId);
        });
    }
    /**
     * An in-progress purchase cannot continue once its product is inactive.
     * The customer starts again from the catalog.
     */
    function blockInactivePurchase() {
        alert.present({
            title: React.createElement("p", null, t('This product is no longer available.'))
        });
        setStoreItems([]);
        setDealId(null);
        clearDealIdFromUrl();
        setCurrentStepId('products');
    }
    function clearDealIdFromUrl() {
        const params = new URLSearchParams(location.search);
        params.delete('dealId');
        window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
    }
    /**
     * @function handleDealUpdate
     * @since 1.0.0
     * @hidden
     */
    function handleDealUpdate(updatedDeal) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!updatedDeal.id) {
                return;
            }
            setIsUpdatingDeal(true);
            try {
                yield updateDeal({
                    variables: {
                        input: buildStoreDealUpdateInput(updatedDeal)
                    }
                });
                yield refetchDeal();
            }
            catch (error) {
                if (isProductInactiveError(error)) {
                    blockInactivePurchase();
                }
                else if (!handleOutOfStock(error)) {
                    alert.present({
                        title: React.createElement("p", null, t('Order cannot be saved'))
                    });
                }
            }
            finally {
                setIsUpdatingDeal(false);
            }
        });
    }
    /**
     * Une rupture de stock refusée par le serveur : on prévient le client et on
     * recharge le stock pour que les quantités affichées soient à jour.
     * @function handleOutOfStock
     * @since 1.0.0
     * @hidden
     */
    function handleOutOfStock(error) {
        if (!outOfStockFields(error)) {
            return false;
        }
        alert.present({
            title: (React.createElement("p", null, t('Some products are no longer available in the requested quantity. Please review your order.')))
        });
        refetchItems();
        return true;
    }
    /**
     * @function handleRemoveItem
     * @since 1.0.0
     * @hidden
     */
    function handleRemoveItem(item) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!deal) {
                return;
            }
            const activeItems = (_b = (_a = deal.items) === null || _a === void 0 ? void 0 : _a.filter((entry) => !entry.proposed)) !== null && _b !== void 0 ? _b : [];
            const isLastItem = activeItems.length <= 1;
            const updatedDeal = removeDealItem(deal, item.id);
            if (isLastItem) {
                setIsUpdatingDeal(true);
                try {
                    yield updateDeal({
                        variables: {
                            input: buildStoreDealUpdateInput(updatedDeal)
                        }
                    });
                }
                catch (error) {
                    if (isProductInactiveError(error)) {
                        blockInactivePurchase();
                    }
                    else {
                        alert.present({
                            title: React.createElement("p", null, t('Order cannot be saved'))
                        });
                    }
                    setIsUpdatingDeal(false);
                    return;
                }
                setDealId(null);
                clearDealIdFromUrl();
                setStoreItems([]);
                setIsUpdatingDeal(false);
                goToStep('products');
                return;
            }
            yield handleDealUpdate(updatedDeal);
        });
    }
    /**
     * @function handleRemoveOption
     * @since 1.0.0
     * @hidden
     */
    function handleRemoveOption(item, option) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!deal || option.included) {
                return;
            }
            yield handleDealUpdate(removeDealItemOption(deal, item.id, option.id));
        });
    }
    /**
     * @function syncDealIdInUrl
     * @since 1.0.0
     * @hidden
     */
    function syncDealIdInUrl(id) {
        const params = new URLSearchParams(location.search);
        params.set('dealId', id);
        window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
    }
    function onConfirmationClose() {
        const params = new URLSearchParams(location.search);
        params.delete('dealId');
        params.delete('session_id');
        params.delete('payment_id');
        window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
        setDealId(null);
        setStoreItems([]);
        setPaymentComplete(false);
        setCheckoutSessionKey(null);
        formValuesRef.current = null;
        stripeReturnHandledRef.current = false;
        beginCheckoutSentRef.current = false;
        purchaseSentRef.current = false;
        restorePaymentStepRef.current = true;
        hydratedFromDealRef.current = false;
        clearStoreCart(storeCartHost);
        goToStep('products');
    }
    /**
     * @function onApproveDealProcess
     * @since 1.0.0
     * @hidden
     */
    function formString(values, key) {
        const value = values[key];
        return typeof value === 'string'
            ? value
            : value != null
                ? String(value)
                : '';
    }
    function onApproveDealProcess(values) {
        var _a, _b;
        formValuesRef.current = values;
        return {
            variables: {
                input: Object.assign(Object.assign({ id: dealId, firstName: formString(values, 'firstName') || ((_a = deal === null || deal === void 0 ? void 0 : deal.contact) === null || _a === void 0 ? void 0 : _a.firstname) || '', lastName: formString(values, 'lastName') || ((_b = deal === null || deal === void 0 ? void 0 : deal.contact) === null || _b === void 0 ? void 0 : _b.lastname) || '', email: orderEmail || formString(values, 'email') || '', date: deal === null || deal === void 0 ? void 0 : deal.date, signatureBase64: 'direct-sale', publicUserIp: '1.1.1.1', directSale: true }, buildGiftDealInput(values)), { plannedDate: formString(values, 'plannedDate') || undefined, clientName: formString(values, 'name') || undefined, location: buildStripeLocation(values) })
            },
            refetchQueries: [
                {
                    query: DealPublicQuery,
                    variables: { input: { id: dealId !== null && dealId !== void 0 ? dealId : '' } }
                }
            ],
            awaitRefetchQueries: true
        };
    }
    function buildStripeLocation(values) {
        const civicNumber = values ? formString(values, 'civicNumber') : '';
        if (!civicNumber || !(dynamicSalesFormContactFields === null || dynamicSalesFormContactFields === void 0 ? void 0 : dynamicSalesFormContactFields.includes('ADDRESS'))) {
            return undefined;
        }
        return {
            civicNumber,
            road: formString(values, 'road'),
            apartment: formString(values, 'apartment'),
            city: formString(values, 'city'),
            province: formString(values, 'province'),
            postalcode: formString(values, 'postalcode'),
            country: formString(values, 'country'),
            latitude: Number(formString(values, 'latitude')),
            longitude: Number(formString(values, 'longitude')),
            billing: true,
            shipping: true,
            primary: true
        };
    }
    function onApproveDealValidate(values) {
        var _a;
        if (requiresAddress && !addressFilled) {
            setAddressError(true);
            (_a = document.getElementById('store-address-field')) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            throw new ApolloError({
                errorMessage: t('Please enter your address to continue.')
            });
        }
        setAddressError(false);
        if (showPlannedDate && !formString(values, 'plannedDate').trim()) {
            throw new ApolloError({
                errorMessage: t('Please enter your desired date to continue.')
            });
        }
        const giftError = validateGiftDealInput(values);
        if (giftError) {
            throw new ApolloError({
                errorMessage: giftError
            });
        }
    }
    /**
     * @function onApproveDealSuccess
     * @since 1.0.0
     * @hidden
     */
    function onApproveDealSuccess() {
        restorePaymentStepRef.current = true;
        goToStep('payment');
    }
    function confirmLeaveToProducts() {
        if (!dealId) {
            return true;
        }
        return window.confirm(t('Your order progress will be kept. To change products, finish or cancel this order first.'));
    }
    function handleNavigateToStep(stepId) {
        if (stepId === currentStepId || showConfirmationStep) {
            return;
        }
        const currentIndex = storeSteps.findIndex((step) => step.id === currentStepId);
        const targetIndex = storeSteps.findIndex((step) => step.id === stepId);
        if (targetIndex < 0 || targetIndex >= currentIndex) {
            return;
        }
        if (stepId === 'products' && !confirmLeaveToProducts()) {
            return;
        }
        goToStep(stepId);
    }
    function handleLogoClick() {
        var _a;
        if (showConfirmationStep) {
            window.location.assign(`/${storeHomePath(language, partnerSlug)}`);
            return;
        }
        if (currentStepId === 'products') {
            for (const selector of ['.store-screen', '.public-layout']) {
                (_a = document.querySelector(selector)) === null || _a === void 0 ? void 0 : _a.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            return;
        }
        handleNavigateToStep('products');
    }
    function handleBack() {
        if (prevStepId) {
            handleNavigateToStep(prevStepId);
        }
    }
    function persistSelectionAndGoTo(stepId, unitsByLine) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!dealId) {
                goToStep(stepId);
                return;
            }
            if (!dealReady) {
                return;
            }
            if (!deal) {
                openEmailModal();
                return;
            }
            setIsUpdatingDeal(true);
            try {
                yield updateDeal({
                    variables: {
                        input: buildStoreDealUpdateInput(applyStoreSelectionToDeal(deal, storeItems, saleItems), unitsByLine)
                    }
                });
                yield refetchDeal();
                goToStep(stepId);
            }
            catch (error) {
                if (isProductInactiveError(error)) {
                    blockInactivePurchase();
                }
                else if (!handleOutOfStock(error)) {
                    alert.present({
                        title: React.createElement("p", null, t('Order cannot be saved'))
                    });
                }
            }
            finally {
                setIsUpdatingDeal(false);
            }
        });
    }
    function handleOptionsContinue() {
        return __awaiter(this, void 0, void 0, function* () {
            if (isUpdatingDeal) {
                return;
            }
            if (dealId) {
                yield persistSelectionAndGoTo(afterSelectionStepId);
                return;
            }
            openEmailModal();
        });
    }
    /**
     * Saves the answers of each voucher, then goes to the order step.
     * @function handleDetailsContinue
     * @since 1.0.0
     * @hidden
     */
    function handleDetailsContinue(unitsByLine) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isUpdatingDeal) {
                return;
            }
            yield persistSelectionAndGoTo('contact', unitsByLine);
        });
    }
    /**
     * @function onFailure
     * @since 1.0.0
     * @hidden
     */
    const onFailure = (error) => {
        if (isProductInactiveError(error)) {
            blockInactivePurchase();
            return;
        }
        if (handleOutOfStock(error)) {
            return;
        }
        if (isVoucherFieldsMissingError(error)) {
            alert.present({
                title: React.createElement("p", null, t('Please fill in the required fields (*).'))
            });
            goToStep('details');
            return;
        }
        const promoReason = promoCodeErrorReason(error);
        if (promoReason) {
            void handlePromoCodeInvalid(promoReason);
            return;
        }
        alert.present({ error });
    };
    const captchaConfig = captcha !== null && captcha !== void 0 ? captcha : {
        enabled: false,
        provider: 'turnstile',
        siteKey: null
    };
    /**
     * The totals changed: on the payment step, a new Stripe session is needed
     * for the new amount.
     * @function handlePromoCodeChange
     * @since 1.0.0
     * @hidden
     */
    function handlePromoCodeChange(updatedDeal) {
        clearStorePromoCode(host);
        if (currentStepId === 'payment') {
            setCheckoutSessionKey(`${updatedDeal.id}-${Date.now()}`);
        }
    }
    /**
     * The code expired or ran out after it was applied: tell the customer and
     * take it off the cart, so the order can go on at the full price.
     * @function handlePromoCodeInvalid
     * @since 1.0.0
     * @hidden
     */
    function handlePromoCodeInvalid(reason) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            alert.present({
                title: (React.createElement("p", null,
                    promoCodeReasonMessage(reason !== null && reason !== void 0 ? reason : ''),
                    ' ',
                    t('It was removed from your order.')))
            });
            if (!dealId) {
                return;
            }
            try {
                const result = yield removePromoCode({
                    variables: { input: { id: dealId } }
                });
                if ((_a = result.data) === null || _a === void 0 ? void 0 : _a.removePromoCode) {
                    handlePromoCodeChange(result.data.removePromoCode);
                }
            }
            catch (_b) {
                yield refetchDeal();
            }
        });
    }
    const canEditPromoCode = !!deal &&
        !!dealId &&
        !paymentComplete &&
        deal.status !== 'WON' &&
        deal.status !== 'LOST' &&
        deal.stage !== 'COMPLETED';
    const promoCodeField = canEditPromoCode && deal ? (React.createElement(StorePromoCodeField, { deal: deal, captcha: captchaConfig, language: language, initialCode: deal.promoCode ? undefined : loadStorePromoCode(host), disabled: isUpdatingDeal, onChange: handlePromoCodeChange })) : null;
    useEffect(() => {
        if (dealId &&
            (currentStepId === 'contact' || currentStepId === 'payment')) {
            syncDealIdInUrl(dealId);
        }
    }, [dealId, currentStepId]);
    useEffect(() => {
        if (paymentComplete ||
            (deal === null || deal === void 0 ? void 0 : deal.status) === 'WON' ||
            (deal === null || deal === void 0 ? void 0 : deal.status) === 'LOST' ||
            (deal === null || deal === void 0 ? void 0 : deal.stage) === 'COMPLETED') {
            clearStoreCart(storeCartHost);
            return;
        }
        saveStoreCart(storeCartHost, { dealId, items: storeItems });
    }, [
        dealId,
        storeItems,
        storeCartHost,
        paymentComplete,
        deal === null || deal === void 0 ? void 0 : deal.status,
        deal === null || deal === void 0 ? void 0 : deal.stage
    ]);
    useEffect(() => {
        if (!dealId || !dealError) {
            return;
        }
        setDealId(null);
        clearDealIdFromUrl();
    }, [dealId, dealError]);
    useEffect(() => {
        if (!deal || hydratedFromDealRef.current) {
            return;
        }
        if (deal.status === 'WON' ||
            deal.status === 'LOST' ||
            deal.stage === 'COMPLETED') {
            hydratedFromDealRef.current = true;
            return;
        }
        if (storeItems.length > 0) {
            hydratedFromDealRef.current = true;
            return;
        }
        const items = selectedItemsFromDeal(deal);
        if (items.length > 0) {
            setStoreItems(items);
        }
        hydratedFromDealRef.current = true;
    }, [deal, storeItems.length]);
    useEffect(() => {
        if (!deal) {
            return;
        }
        if (!restorePaymentStepRef.current) {
            return;
        }
        if (paymentComplete ||
            deal.status === 'WON' ||
            deal.stage === 'COMPLETED') {
            setCurrentStepId('payment');
            if (deal.status === 'WON' || deal.stage === 'COMPLETED') {
                setPaymentComplete(true);
            }
            return;
        }
        if (deal.stage === 'PENDING_PAYMENT' && !paymentComplete) {
            setCurrentStepId('payment');
        }
    }, [deal, paymentComplete]);
    useEffect(() => {
        var _a;
        if (!dealId ||
            !(deal === null || deal === void 0 ? void 0 : deal.directSale) ||
            sessionId ||
            paymentComplete ||
            deal.status === 'WON' ||
            deal.status === 'LOST' ||
            deal.stage === 'COMPLETED') {
            return;
        }
        if (!directSaleCartHasInactiveProduct((_a = deal.items) !== null && _a !== void 0 ? _a : [])) {
            return;
        }
        blockInactivePurchase();
    }, [deal, dealId, paymentComplete, sessionId]);
    useEffect(() => {
        if (currentStepId !== 'payment') {
            setCheckoutSessionKey(null);
            return;
        }
        if (paymentComplete ||
            !deal ||
            deal.stage !== 'PENDING_PAYMENT' ||
            !hasActivePayments) {
            return;
        }
        setCheckoutSessionKey((current) => current !== null && current !== void 0 ? current : `${deal.id}-${Date.now()}`);
    }, [
        currentStepId,
        paymentComplete,
        deal,
        deal === null || deal === void 0 ? void 0 : deal.stage,
        deal === null || deal === void 0 ? void 0 : deal.id,
        hasActivePayments
    ]);
    useEffect(() => {
        if (!sessionId || !dealId || stripeReturnHandledRef.current) {
            return;
        }
        stripeReturnHandledRef.current = true;
        fetch(process.env.API_URL_ROOT +
            `/stripe-session-status?id=${dealId}&session_id=${sessionId}&payment_id=${paymentId !== null && paymentId !== void 0 ? paymentId : ''}`, { method: 'POST' })
            .then((res) => res.json())
            .then(() => {
            setPaymentComplete(true);
            setCurrentStepId('payment');
            const params = new URLSearchParams(location.search);
            params.delete('session_id');
            params.delete('payment_id');
            window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
        })
            .catch((error) => {
            alert.present({ error });
        });
    }, [sessionId, dealId, paymentId]);
    useEffect(() => {
        if (currentStepId !== 'payment' ||
            paymentComplete ||
            (deal === null || deal === void 0 ? void 0 : deal.stage) === 'PENDING_PAYMENT') {
            return;
        }
        const timer = window.setTimeout(() => {
            refetchDeal();
        }, 500);
        return () => window.clearTimeout(timer);
    }, [currentStepId, paymentComplete, deal === null || deal === void 0 ? void 0 : deal.stage, dealId]);
    useEffect(() => {
        var _a, _b, _c;
        if (!options.gtmId ||
            !deal ||
            currentStepId !== 'payment' ||
            !showPaymentStep ||
            !isOnlinePayment ||
            !hasActivePayments ||
            beginCheckoutSentRef.current) {
            return;
        }
        beginCheckoutSentRef.current = true;
        sendStoreGTMEvent('begin_checkout', {
            value: deal.total || deal.subtotal || 0,
            currency: ((_a = deal.currency) === null || _a === void 0 ? void 0 : _a.code) || 'CAD',
            items: buildStoreGTMItems((_b = deal.items) !== null && _b !== void 0 ? _b : [], ((_c = deal.currency) === null || _c === void 0 ? void 0 : _c.code) || 'CAD', deal.language || language)
        });
    }, [currentStepId, showPaymentStep, deal, options.gtmId, language]);
    useEffect(() => {
        var _a, _b, _c;
        if (!options.gtmId ||
            !deal ||
            !paymentComplete ||
            purchaseSentRef.current) {
            return;
        }
        purchaseSentRef.current = true;
        pushEnhancedConversionUserData(host, { email: orderEmail });
        sendStoreGTMEvent('purchase', {
            transaction_id: deal.id,
            value: deal.total || deal.subtotal || 0,
            currency: ((_a = deal.currency) === null || _a === void 0 ? void 0 : _a.code) || 'CAD',
            items: buildStoreGTMItems((_b = deal.items) !== null && _b !== void 0 ? _b : [], ((_c = deal.currency) === null || _c === void 0 ? void 0 : _c.code) || 'CAD', deal.language || language)
        });
    }, [paymentComplete, deal, options.gtmId, language]);
    if (isPublicAccountUnavailableError(saleItemsError) ||
        isPublicAccountUnavailableError(dealError) ||
        (hasStoreForm !== 'TRUE' && hasDirectSales !== 'TRUE')) {
        return (React.createElement(EmptyData, { title: t('This form is not available.'), description: t('Please contact us for more information.') }));
    }
    return (React.createElement(Screen, { className: classNames('store-screen', {
            'store-screen--checkout-step': currentStepId === 'contact'
        }), ready: !partnerSlug || !!partnerStore },
        React.createElement(Content, null,
            React.createElement(Header, null,
                React.createElement(StoreHeader, { configurationFields: configurationFields, logo: logo, partnerSlug: partnerStore ? partnerSlug : null, currentStepId: currentStepId, documentTitle: documentTitle, steps: storeSteps, isSuccessScreen: showConfirmationStep, onLogoClick: handleLogoClick, onStepClick: showConfirmationStep ? undefined : handleNavigateToStep })),
            currentStepId === 'products' && (React.createElement(ProductSection, { data: data, ready: data != null, lang: language, hasDirectSales: hasDirectSales === 'TRUE', setHasOnlyFinishedProducts: setHasOnlyFinishedProducts, saleItemGroup: dynamicSalesFormSaleItemGroup, showProposedOptions: false, selectedItems: storeItems, onNext: handleProductsContinue, onSelectionChange: (items) => setStoreItems(items), showFormInputs: false, showCategoryNav: true, disableAutoSelect: storeItems.length > 0 || !!dealId })),
            currentStepId === 'options' && (React.createElement(StoreProposedOptionsSection, { lang: language, hasDirectSales: hasDirectSales === 'TRUE', saleItems: saleItems, selectedItems: storeItems, onSelectionChange: (items) => setStoreItems(items), processing: isUpdatingDeal, onNext: handleOptionsContinue, onBack: handleBack })),
            currentStepId === 'details' && dealId && (React.createElement(StoreVoucherFieldsSection, { dealId: dealId, saleItems: saleItems, processing: isUpdatingDeal, onNext: (unitsByLine) => void handleDetailsContinue(unitsByLine), onBack: handleBack })),
            currentStepId === 'contact' && (React.createElement(MutationForm, { name: "store-order", mutation: ApproveDealMutation, onProcess: onApproveDealProcess, onValidate: onApproveDealValidate, onSuccess: onApproveDealSuccess, onFailure: onFailure },
                React.createElement("div", { className: "store-checkout-layout store-checkout-layout--step2" },
                    React.createElement("div", { className: "store-checkout-layout__form" },
                        React.createElement(ContactSection, { contactFields: dynamicSalesFormContactFields, onAddressFilled: setAddressFilled, orderEmail: orderEmail, addressError: addressError, showPlannedDate: showPlannedDate, hidePartnerField: !!partnerStore, onBack: handleBack }),
                        React.createElement("div", { className: "store-checkout-submit" },
                            React.createElement("div", { className: "store-checkout-submit__actions" },
                                React.createElement(Button, { style: "primary", size: "large", className: "store-checkout-submit__pay", picto: isOnlinePayment && hasActivePayments
                                        ? 'lock'
                                        : undefined, label: checkoutSubmitLabel, submit: true })))),
                    React.createElement("div", { className: "store-checkout-layout__summary" },
                        React.createElement(StoreQuoteSummaryPanel, { deal: deal, configurationFields: configurationFields, saleItems: saleItems, defaultCollapsed: false, editable: !!dealId, isUpdating: isUpdatingDeal, onRemoveItem: handleRemoveItem, onRemoveOption: handleRemoveOption, promoCodeField: promoCodeField }))),
                React.createElement(StoreCheckoutStickyBar, { deal: deal, submitLabel: checkoutSubmitLabel, showLockIcon: isOnlinePayment && hasActivePayments }))),
            showCheckoutLoading && (React.createElement("div", { className: "store-checkout-loading" },
                React.createElement(Spinner, null),
                React.createElement("p", null, t('Preparing secure checkout…')))),
            showPaymentStep && deal && checkoutSessionKey && (React.createElement(StorePaymentPanel, { deal: deal, returnPath: stripeReturnPath, checkoutSessionKey: checkoutSessionKey, configurationFields: configurationFields, saleItems: saleItems, editable: !!dealId, isUpdating: isUpdatingDeal, onRemoveItem: handleRemoveItem, onRemoveOption: handleRemoveOption, onBack: handleBack, onOutOfStock: () => {
                    handleOutOfStock({ error: 'OUT_OF_STOCK', fields: {} });
                    goToStep('products');
                }, onProductInactive: blockInactivePurchase, onPromoCodeInvalid: (reason) => {
                    void handlePromoCodeInvalid(reason);
                }, promoCodeField: promoCodeField, onPaymentSimulated: () => {
                    setPaymentComplete(true);
                    goToStep('payment');
                    refetchDeal();
                }, onPaymentCompleted: () => {
                    setPaymentComplete(true);
                    goToStep('payment');
                    refetchDeal();
                }, language: language, contactValues: {
                    firstName: (formValuesRef.current &&
                        formString(formValuesRef.current, 'firstName')) ||
                        ((_r = deal.contact) === null || _r === void 0 ? void 0 : _r.firstname) ||
                        undefined,
                    lastName: (formValuesRef.current &&
                        formString(formValuesRef.current, 'lastName')) ||
                        ((_s = deal.contact) === null || _s === void 0 ? void 0 : _s.lastname) ||
                        undefined,
                    phone: (formValuesRef.current &&
                        formString(formValuesRef.current, 'phone')) ||
                        undefined,
                    location: buildStripeLocation(formValuesRef.current)
                } })),
            showConfirmationStep && (React.createElement(StoreConfirmation, { deal: deal, hasPayment: paymentComplete })),
            React.createElement("div", { className: "store-screen-footer" },
                React.createElement("div", { className: "store-screen-footer--links" },
                    React.createElement("div", { className: "store-screen-footer--lang-switcher" },
                        React.createElement("a", { href: `/${storeLanguageSwitchPath(language, partnerSlug)}` },
                            React.createElement(Icon, { value: "globe" }),
                            React.createElement(Text, { value: language === 'fr' ? 'English' : 'Français' }))),
                    React.createElement("a", { href: "/vouchers/status", className: "store-screen-footer--voucher-status" }, t('Check my balance'))),
                React.createElement("div", { className: "store-screen-powered" },
                    React.createElement("div", { className: "store-screen-powered-logo" },
                        React.createElement("a", { href: "https://centralio.com/voucher-plus", target: "_blank", rel: "noreferrer" },
                            React.createElement("div", { className: "store-screen-powered-text" },
                                t('Voucher Plus'),
                                " \u2122 ",
                                t('by')),
                            React.createElement("img", { src: "/images/icon.svg" }),
                            React.createElement("span", { className: "store-screen-powered-centralio" }, "Centralio"))))))));
}
